import { z } from 'zod';
import {
  ageSchema,
  entityIdSchema,
  isoDateTimeSchema,
  localeSchema,
  localizedTextSchema,
  safeTextSchema,
  uniqueArraySchema,
} from './common';

/** Editorial content item (programme, episode, short) and its publication metadata. */

export const CONTENT_FORMATS = ['episode', 'short', 'special', 'clip'] as const;
export const contentFormatSchema = z.enum(CONTENT_FORMATS);
export type ContentFormat = z.infer<typeof contentFormatSchema>;

export const PUBLISH_STATES = ['draft', 'published', 'retracted'] as const;
export const publishStateSchema = z.enum(PUBLISH_STATES);
export type PublishState = z.infer<typeof publishStateSchema>;

/** Broad, child-selectable interests used for deterministic filtering (no personalised ranking). */
export const INTEREST_TOPICS = [
  'science',
  'nature',
  'stories',
  'making',
  'music',
  'sport',
  'history',
  'animals',
] as const;
export const interestTopicSchema = z.enum(INTEREST_TOPICS);
export type InterestTopic = z.infer<typeof interestTopicSchema>;

export const CONTENT_WARNINGS = [
  'mild-peril',
  'loud-noises',
  'flashing-lights',
  'darkness',
] as const;
export const contentWarningSchema = z.enum(CONTENT_WARNINGS);

export const CREDIT_ROLES = [
  'production',
  'commissioning-member',
  'creator-collaboration',
  'voice',
  'music',
] as const;
export const creditSchema = z.strictObject({
  name: safeTextSchema(80),
  role: z.enum(CREDIT_ROLES),
});
export type Credit = z.infer<typeof creditSchema>;

export const seriesRefSchema = z.strictObject({
  seriesId: entityIdSchema,
  season: z.int().min(1).max(99),
  episode: z.int().min(1).max(999),
});
export type SeriesRef = z.infer<typeof seriesRefSchema>;

/** Field shape without cross-field rules, so editorial input schemas can pick/omit fields. */
export const contentItemFieldsSchema = z.strictObject({
  id: entityIdSchema,
  originBroadcasterId: entityIdSchema,
  credits: z.array(creditSchema).max(20),
  series: seriesRefSchema.nullable(),
  format: contentFormatSchema,
  title: localizedTextSchema(120),
  description: localizedTextSchema(600),
  ageMin: ageSchema,
  ageMax: ageSchema,
  contentWarnings: uniqueArraySchema(contentWarningSchema).max(CONTENT_WARNINGS.length),
  topics: uniqueArraySchema(interestTopicSchema).min(1).max(INTEREST_TOPICS.length),
  durationSeconds: z
    .int()
    .min(1)
    .max(4 * 3600),
  publishState: publishStateSchema,
  publishedAt: isoDateTimeSchema.nullable(),
  /** Locales with an actual caption/subtitle track; never claim tracks that do not exist. */
  captionLocales: uniqueArraySchema(localeSchema),
  /**
   * Spoken-audio languages actually present (no implied dubbing). Empty = no spoken words (music,
   * sounds and on-screen text only; migration 0005).
   */
  audioLocales: uniqueArraySchema(localeSchema),
  missionId: entityIdSchema.nullable(),
  mediaAssetId: entityIdSchema.nullable(),
  /** Fictional demonstration material must be labelled as such in every UI. */
  isFictional: z.boolean(),
  version: z.int().min(1),
});

/** Age range must be ordered; shared by content, missions and their editorial inputs. */
export const ageRangeRule: [
  (v: { ageMin: number; ageMax: number }) => boolean,
  { message: string; path: string[] },
] = [(v) => v.ageMin <= v.ageMax, { message: 'ageMin must not exceed ageMax', path: ['ageMin'] }];

export const contentItemSchema = contentItemFieldsSchema
  .refine(...ageRangeRule)
  .refine((c) => c.publishState !== 'published' || c.publishedAt !== null, {
    message: 'Published items need publishedAt',
    path: ['publishedAt'],
  });
export type ContentItem = z.infer<typeof contentItemSchema>;
